import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Toppers Notes - Best Short Science Notes for Class 10th 2025-2026 | TOPPERS BUDDY',
  description = 'Best notes for class 10th science ⭐ Toppers short notes trusted by 1,300+ students. Science notes class 10 with diagrams, formulas for Physics, Chemistry, Biology. CBSE board exam 2025-2026. Only ₹149. Download now!',
  keywords = 'toppers notes, toppers short notes, toppers handwritten notes, class 10th notes, class 10th science notes, science notes class 10, best notes for class 10th science, class 10 science notes, short notes class 10, topper notes for board exam, CBSE class 10 science notes, class 10 physics notes, class 10 chemistry notes, class 10 biology notes, board exam preparation notes, science notes PDF, NCERT science notes class 10, toppers buddy, 10th class science notes, science short notes, quick revision notes class 10, exam preparation notes, board exam notes 2025, board exam notes 2026, class 10 topper notes, best study material class 10',
  canonicalUrl = 'https://toppersbuddy.lovable.app',
  ogImage = '/favicon.jpg',
  ogType = 'website',
  noIndex = false,
}) => {
  const siteUrl = 'https://toppersbuddy.lovable.app';
  const fullCanonicalUrl = canonicalUrl.startsWith('http') ? canonicalUrl : `${siteUrl}${canonicalUrl}`;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  // Website Schema with SearchAction for sitelinks
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'TOPPERS BUDDY',
    alternateName: ['Toppers Notes', 'Toppers Buddy Notes', 'Class 10 Science Notes'],
    url: siteUrl,
    description: 'Best notes for class 10th science - Toppers short notes for CBSE board exams',
    inLanguage: ['en-IN', 'hi-IN'],
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Class 10 Science Notes',
        item: siteUrl,
      },
    ],
  };

  // Product Schema for rich snippets
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Toppers Short Notes - Class 10th Science Notes 2025-2026',
    description: 'Best notes for class 10th science - Toppers short notes for CBSE board exams. Science notes class 10 with complete physics, chemistry, and biology. Class 10th notes trusted by 1300+ students.',
    image: fullOgImage,
    brand: {
      '@type': 'Brand',
      name: 'TOPPERS BUDDY',
    },
    sku: 'TB-CLASS10-SCI-2025',
    mpn: 'TOPPERS-BUDDY-C10',
    category: 'Educational Materials > Study Notes > Class 10',
    offers: {
      '@type': 'Offer',
      price: '149',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
      priceValidUntil: '2026-03-31',
      url: fullCanonicalUrl,
      seller: {
        '@type': 'Organization',
        name: 'TOPPERS BUDDY',
      },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '1300',
      bestRating: '5',
      worstRating: '1',
    },
    review: [
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Arjun Sharma' },
        datePublished: '2024-12-15',
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        reviewBody: 'These notes are a lifesaver! The content is neat and all important topics are covered. Scored 95 in Science!',
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Priya Patel' },
        datePublished: '2024-12-10',
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        reviewBody: 'Best investment for board exams. The diagrams and formulas are so well organized. Highly recommend!',
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Rahul Kumar' },
        datePublished: '2024-12-05',
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        reviewBody: 'Revised the entire syllabus in just 2 days before exams. These notes made it possible. Thank you!',
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Sneha Verma' },
        datePublished: '2024-11-28',
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        reviewBody: 'Perfect for last minute revision. All formulas and diagrams in one place. Got 92 marks!',
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Vikash Singh' },
        datePublished: '2024-11-20',
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        reviewBody: 'Best notes I have ever used. Short, crisp and covers everything for board exams.',
      },
    ],
  };

  // Organization Schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'TOPPERS BUDDY',
    alternateName: 'Toppers Notes',
    url: siteUrl,
    logo: fullOgImage,
    description: 'Premium short study notes for Class 10 CBSE students - Best notes for class 10th science',
    foundingDate: '2024',
    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['English', 'Hindi'],
      areaServed: 'IN',
    },
  };

  // FAQ Schema for "People Also Ask"
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What are the best notes for class 10th science?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Toppers short notes from TOPPERS BUDDY are the best notes for class 10th science. These class 10th notes are trusted by 1,300+ students and include all key topics, diagrams, formulas for Physics, Chemistry, and Biology. Available at just ₹149.',
        },
      },
      {
        '@type': 'Question',
        name: 'Where can I get toppers notes for class 10?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can get toppers notes for class 10 from TOPPERS BUDDY at toppersbuddy.lovable.app. Our toppers short notes are available at just ₹149 and cover the entire class 10th science syllabus with diagrams and formulas.',
        },
      },
      {
        '@type': 'Question',
        name: 'Are toppers short notes good for class 10th board exams 2025-2026?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! Toppers short notes are excellent for class 10th board exams 2025-2026. Science notes class 10 from TOPPERS BUDDY are written by students who scored 95+ marks. These class 10th notes help you revise the entire syllabus quickly.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the price of class 10th science notes?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Toppers short notes for class 10th science are available at just ₹149 (originally ₹499 - 70% OFF). These class 10th notes include complete Physics, Chemistry, and Biology with diagrams and formulas. Best notes for class 10th science at affordable price.',
        },
      },
      {
        '@type': 'Question',
        name: 'How to get science notes class 10 for quick revision?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Get science notes class 10 from TOPPERS BUDDY for quick revision. Our toppers notes are designed so you can revise the entire class 10th science syllabus in just a few hours before exams. Visit toppersbuddy.lovable.app to buy now.',
        },
      },
      {
        '@type': 'Question',
        name: 'What topics are covered in toppers notes for class 10 science?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Toppers notes for class 10 science cover all NCERT topics including Physics (Light, Electricity, Magnetic Effects), Chemistry (Chemical Reactions, Acids Bases Salts, Metals Non-metals, Carbon Compounds), and Biology (Life Processes, Control and Coordination, Heredity, Environment). All diagrams and formulas included.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is TOPPERS BUDDY notes available in PDF format?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, TOPPERS BUDDY science notes class 10 are available in digital format. After purchase at ₹149, you get instant access to all toppers short notes that you can use for quick revision before board exams.',
        },
      },
    ],
  };

  // Educational Course Schema
  const courseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: 'Toppers Short Notes - Class 10th Science Notes 2025-2026',
    description: 'Best notes for class 10th science - Toppers short notes covering Physics, Chemistry, and Biology. Science notes class 10 for CBSE board exam preparation 2025-2026.',
    provider: {
      '@type': 'Organization',
      name: 'TOPPERS BUDDY',
      sameAs: siteUrl,
    },
    educationalLevel: 'Class 10 / Grade 10',
    about: ['Physics Class 10', 'Chemistry Class 10', 'Biology Class 10', 'Science CBSE'],
    inLanguage: ['en', 'hi'],
    offers: {
      '@type': 'Offer',
      price: '149',
      priceCurrency: 'INR',
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
    },
  };

  // HowTo Schema for buying process
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Get Toppers Notes for Class 10 Science',
    description: 'Simple steps to buy best notes for class 10th science from TOPPERS BUDDY',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Visit Website',
        text: 'Go to toppersbuddy.lovable.app to see toppers short notes',
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Click Buy Now',
        text: 'Click on Buy Now button to purchase class 10th science notes at ₹149',
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Complete Payment',
        text: 'Make secure payment using UPI, Card, or Net Banking',
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Get Instant Access',
        text: 'Receive toppers notes instantly and start revision for board exams',
      },
    ],
    totalTime: 'PT5M',
  };

  // LocalBusiness Schema for local SEO
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'TOPPERS BUDDY',
    alternateName: 'Toppers Notes India',
    url: siteUrl,
    logo: fullOgImage,
    description: 'Best notes for class 10th science - Toppers short notes for CBSE board exams in India',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'IN',
    },
    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
    priceRange: '₹149',
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="TOPPERS BUDDY" />
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large" />
      
      {/* Geo Tags for India targeting */}
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="geo.position" content="20.5937;78.9629" />
      <meta name="ICBM" content="20.5937, 78.9629" />
      <meta name="language" content="English" />
      <meta name="distribution" content="India" />
      <meta name="target" content="all" />
      <meta name="audience" content="students" />
      <meta name="coverage" content="India" />
      
      {/* Content Tags */}
      <meta name="classification" content="Education, Study Materials, Class 10 Notes" />
      <meta name="subject" content="Class 10 Science Notes, CBSE Board Exam Preparation" />
      <meta name="topic" content="Toppers Notes, Class 10th Science Notes, Board Exam Notes" />
      <meta name="summary" content="Best notes for class 10th science - Toppers short notes at ₹149" />
      
      {/* Revisit and Cache */}
      <meta name="revisit-after" content="7 days" />
      <meta name="expires" content="never" />
      <meta name="rating" content="General" />
      
      {/* Theme and Mobile */}
      <meta name="theme-color" content="#3b82f6" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="TOPPERS BUDDY" />
      <meta name="application-name" content="TOPPERS BUDDY" />
      <meta name="format-detection" content="telephone=yes" />
      <link rel="apple-touch-icon" href="/favicon.jpg" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Toppers Notes - Best Class 10 Science Notes" />
      <meta property="og:site_name" content="TOPPERS BUDDY" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:locale:alternate" content="hi_IN" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content="Toppers Notes - Best Class 10 Science Notes" />
      <meta name="twitter:site" content="@ToppersBuddy" />
      <meta name="twitter:creator" content="@ToppersBuddy" />
      
      {/* Structured Data / JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(courseSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(howToSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
    </Helmet>
  );
};

export default SEOHead;